const responsiveClass = (desktop, mobile, base = '') => {
  const classes = [base];
  if (desktop && !mobile) classes.push('d-none d-md-table-cell');
  if (!desktop && mobile) classes.push('d-md-none');
  if (!desktop && !mobile) classes.push('d-none');
  return classes.filter(Boolean).join(' ');
};

const field = (column, key, label, baseClass = '') => ({
  key,
  label,
  class: responsiveClass(true, Boolean(column.mobile), baseClass)
});

const configuredField = (key, participantFields, columns) => {
  const configured = participantFields.find((item) => item.key === key);
  if (configured) return configured;

  const column = columns.find((item) => item.key === key) || {};
  return {
    key,
    desktop: column.enabled ? 'column' : 'hidden',
    mobile: column.enabled && column.mobile ? 'column' : 'hidden'
  };
};

const viewportField = (column, participant, key, label, baseClass = '') => ({
  key,
  label,
  class: responsiveClass(
    Boolean(column.enabled) && participant.desktop === 'column',
    Boolean(column.enabled) && participant.mobile === 'column',
    baseClass
  )
});

const courseProgressFields = (race, column, translate) => {
  const baseClass = responsiveClass(true, Boolean(column.mobile), 'font-weight-normal');

  if (race.race_type === 'linear') {
    return (race.splits || [])
      .filter((split) => split.is_split)
      .map((split) => ({
        key: `split_${split.number}`,
        label: split.name,
        colType: 'split',
        class: baseClass,
        split
      }));
  }

  if (race.unwrap_laps && (race.lap_splits || []).length > 0) {
    return race.lap_splits.map((lap) => ({
      key: lap.key,
      label: lap.label,
      colType: 'lap_split',
      class: baseClass,
      lap
    }));
  }

  return [
    field(column, 'result_distance', translate('distance'), 'font-weight-normal'),
    field(column, 'laps', translate('laps.number'), 'font-weight-normal'),
    field(column, 'last_lap', translate('laps.last'), 'font-weight-normal')
  ];
};

const dynamicParticipantColumns = (participantFields, customFields) => {
  const builtInColumns = participantFields
    .filter((item) => !['gender', 'category', 'country'].includes(item.key))
    .map((item) => ({ ...item, sourceType: 'participant' }));
  const customColumns = customFields.map((item) => ({ ...item, sourceType: 'custom' }));

  return builtInColumns.concat(customColumns)
    .filter((item) => item.desktop === 'column' || item.mobile === 'column')
    .map((item) => ({
      key: `${item.sourceType}_${item.key}`,
      label: item.label,
      colType: 'participant_field',
      displayField: item,
      class: responsiveClass(item.desktop === 'column', item.mobile === 'column'),
      formatter: (_value, _key, heat) => {
        const value = item.sourceType === 'custom' ? heat.public_fields?.[item.key] : heat[item.key];
        if (Array.isArray(value)) return value.join(' / ');
        if (value == null || value === '') return '—';
        return String(value);
      }
    }));
};

export function buildRaceResultFields({
  race,
  columns,
  rankLayout = {},
  participantFields = [],
  customFields = [],
  features = {},
  translate
}) {
  const t = translate || ((key) => key);
  const configuredColumns = Array.isArray(columns) ? columns : [];
  const configuredParticipantFields = Array.isArray(participantFields) ? participantFields : [];
  const configuredCustomFields = Array.isArray(customFields) ? customFields : [];
  const fields = [];
  const layouts = {
    desktop: rankLayout.desktop || 'separate',
    mobile: rankLayout.mobile || 'separate'
  };
  const rankKeys = ['rank_abs', 'rank_gender', 'rank_category'];
  const rankLabels = {
    rank_abs: t('ranks.abs'),
    rank_gender: t('ranks.gender'),
    rank_category: t('ranks.category')
  };
  const enabledRankColumns = configuredColumns
    .filter((column) => column.enabled && rankKeys.includes(column.key))
    .filter((column) => column.key !== 'rank_category' || race.has_categories);
  const desktopRankKeys = enabledRankColumns.map((column) => column.key);
  const mobileRankKeys = enabledRankColumns.filter((column) => column.mobile).map((column) => column.key);
  let compactRanksAdded = false;
  let dynamicColumnsAdded = false;

  const compactRankFields = () => {
    const definitions = [];
    if (layouts.desktop === 'compact' && desktopRankKeys.length > 0) {
      definitions.push({ viewport: 'desktop', desktop: true, mobile: false, rankKeys: desktopRankKeys });
    }
    if (layouts.mobile === 'compact' && mobileRankKeys.length > 0) {
      definitions.push({ viewport: 'mobile', desktop: false, mobile: true, rankKeys: mobileRankKeys });
    }

    if (definitions.length === 2 && definitions[0].rankKeys.join() === definitions[1].rankKeys.join()) {
      definitions.splice(0, 2, {
        viewport: 'all', desktop: true, mobile: true, rankKeys: definitions[0].rankKeys
      });
    }

    return definitions.map((definition) => ({
      key: definitions.length === 1 ? 'rank_compact' : `rank_compact_${definition.viewport}`,
      label: definition.rankKeys.map((key) => rankLabels[key]).join('|'),
      colType: 'rank_compact',
      rankKeys: definition.rankKeys,
      class: responsiveClass(definition.desktop, definition.mobile, 'text-center')
    }));
  };

  const addDynamicColumns = () => {
    if (dynamicColumnsAdded) return;
    fields.push(...dynamicParticipantColumns(configuredParticipantFields, configuredCustomFields));
    dynamicColumnsAdded = true;
  };

  if (features.favorites) fields.push({ key: 'compare', label: '' });

  configuredColumns.filter((column) => column.enabled).forEach((column) => {
    if (rankKeys.includes(column.key)) {
      if (!compactRanksAdded) {
        fields.push(...compactRankFields());
        compactRanksAdded = true;
      }

      if (column.key === 'rank_category' && !race.has_categories) return;
      const desktopVisible = layouts.desktop === 'separate';
      const mobileVisible = layouts.mobile === 'separate' && Boolean(column.mobile);
      if (desktopVisible || mobileVisible) {
        fields.push({
          key: column.key,
          label: rankLabels[column.key],
          class: responsiveClass(desktopVisible, mobileVisible, 'text-center')
        });
      }
      return;
    }

    switch (column.key) {
      case 'bib':
        fields.push(field(column, 'bib', t('bib')));
        break;
      case 'gender': {
        const participant = configuredField('gender', configuredParticipantFields, configuredColumns);
        fields.push(viewportField(column, participant, 'gender', ''));
        break;
      }
      case 'name':
        fields.push(field(column, 'name', t('name')));
        addDynamicColumns();
        break;
      case 'category': {
        if (!race.has_categories) break;
        const participant = configuredField('category', configuredParticipantFields, configuredColumns);
        fields.push(viewportField(column, participant, 'category', t('category')));
        break;
      }
      case 'country': {
        const participant = configuredField('country', configuredParticipantFields, configuredColumns);
        fields.push(viewportField(column, participant, 'country', t('country'), 'text-center'));
        break;
      }
      case 'course_progress':
        fields.push(...courseProgressFields(race, column, t));
        break;
      case 'result':
        fields.push(field(column, 'result', t('result'), 'text-center font-weight-bold'));
        break;
      case 'gap':
        fields.push(field(column, 'gap', t('activerecord.attributes.heat.gap'), 'text-center'));
        break;
      case 'diff':
        fields.push(field(column, 'diff', t('activerecord.attributes.heat.diff'), 'text-center'));
        break;
      case 'points':
        fields.push(field(column, 'points', t('points'), 'text-center'));
        break;
      default:
        break;
    }
  });

  addDynamicColumns();
  return fields;
}
